import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout, setLoader, clearFlightSearch } from "../redux/actions";
import { useLocation } from "react-router-dom";
import {
  buildButtonThemeCss,
  isButtonThemePath,
} from "../utils/whitelabelButtonTheme";
import createAxiosInstance from "../request/Axios";
import Loader from "../utils/Loader";
import Footer from "./Footer";
import {
  Features,
  canUseFlightQuotation,
  canUseRebranding,
  isWhitelabel,
  isWhitelabelAdmin,
  getSignInUrl,
} from "../utils/Constants";
import InternetConnectivityCheck from "../utils/InternetConnectivityCheck";

// Types for better type safety
interface MenuItem {
  path: string;
  label: string;
  icon: string;
  dataKey: string;
  onClick?: () => void;
  hideOnMobile?: boolean;
  hidden?: boolean;
}

interface MenuSection {
  id: string;
  title: string;
  icon: string;
  items: MenuItem[];
  condition?: boolean;
}

const AppMenu: React.FC = () => {
  const dispatch = useDispatch();
  const url = useLocation();
  const [isActive, setActiveTab] = useState("");
  const currentUser = useSelector((state: any) => state.currentUser);
  const auth = useSelector((state: any) => state.auth);
  const isInsuranceProvider = localStorage.getItem("_ip") === "1" || String(currentUser?.account_id) === "20248492";
  const sidebarRef = useRef<HTMLDivElement>(null);
  const showPaymentDashboardLink = new Date() >= new Date("2026-07-12"); //date of implementation
  // Hotel Booking v2 (RateHawk/ETG sandbox) is still an internal testing
  // feature — restricted to these accounts until it's ready for everyone.
  // 20244162 is a dedicated test account handed to ETG (the RateHawk API
  // provider) for their own certification testing — see isEtgTestAccount
  // below, which further restricts that one account to seeing only this
  // menu section and nothing else in the app.
  const showHotelV2Menu = ["20242651", "20249329", "20244162"].includes(
    String(currentUser?.account_id)
  );
  // ETG's own test account (account_id 20244162 / user_id 16) — provided to
  // them for certification testing, so it must only ever see Hotel Booking
  // v2, never any other part of the app. Mirrors the isInsuranceProvider
  // pattern below (a swapped-in, minimal menu) rather than hiding dozens of
  // individual items one by one.
  const isEtgTestAccount = String(currentUser?.account_id) === "20244162";
  // Client Inquiry Link (flight quotations / booking inquiries) is a Business-plan
  // perk — same gate as the Create Quote Link button on /flight-booking.
  const showBookingInquiriesMenu = canUseFlightQuotation(currentUser);
  // Not included in the Standard package for new registrations.
  const showRebrandingMenu = canUseRebranding(currentUser);
  // Whitelabel accounts get their own trimmed menu (see whitelabelSections
  // below); the whitelabel admin also gets a scoped Admin Account section.
  const isWhitelabelUser = isWhitelabel(currentUser);
  const isWhitelabelAdminUser = isWhitelabelAdmin(currentUser);
  // The whitelabel ADMIN's own branding (see UserResource::resolveWhitelabelBranding) —
  // applies to the admin's own sidebar and all of its agents' sidebars alike.
  const whitelabelBranding = currentUser?.whitelabel_branding;
  const sidebarLogo =
    (isWhitelabelUser && whitelabelBranding?.logo) ||
    "https://cdn.mywanderlinx.com/assets/images/wanderLinx-banner.png";
  const sidebarBrandName =
    (isWhitelabelUser && whitelabelBranding?.brand_name) || "Menu";
  // The theme's dark-sidebar colors come from CSS custom properties whose
  // actual values/names depend on which build of the vendor CSS is cached
  // in a given browser — overriding the variables themselves (or plain
  // background-color/color inline styles) isn't reliable, since we can't
  // be sure what's actually loaded. An injected <style> block with
  // !important, targeting our own class names directly, wins regardless.
  const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;
  const sidebarNavColor =
    isWhitelabelUser && HEX_COLOR_RE.test(whitelabelBranding?.nav_color || "")
      ? whitelabelBranding!.nav_color
      : null;
  const sidebarFontColor =
    isWhitelabelUser && HEX_COLOR_RE.test(whitelabelBranding?.font_color || "")
      ? whitelabelBranding!.font_color
      : null;
  const sidebarHoverBgColor =
    isWhitelabelUser &&
    HEX_COLOR_RE.test(whitelabelBranding?.hover_bg_color || "")
      ? whitelabelBranding!.hover_bg_color
      : null;
  const sidebarHoverFontColor =
    isWhitelabelUser &&
    HEX_COLOR_RE.test(whitelabelBranding?.hover_font_color || "")
      ? whitelabelBranding!.hover_font_color
      : null;
  // The search pages' "Filters" header card (.flr_note_bck, e.g. on
  // /flight-booking and /hotel-booking) and /hotel-booking's loading
  // placeholder for it (.flr_note_bck_placeholder, which also carries
  // Bootstrap's bg-success !important): its own color from /branding-setup,
  // else the navigation color.
  const filterBgColor = !isWhitelabelUser
    ? null
    : HEX_COLOR_RE.test(whitelabelBranding?.filter_bg_color || "")
    ? whitelabelBranding!.filter_bg_color
    : sidebarNavColor;
  const sidebarThemeCss =
    [
      sidebarNavColor &&
        `.app-menu.navbar-menu, .app-menu .navbar-brand-box, .app-menu a.menu-link, .app-menu .menu-dropdown { background: ${sidebarNavColor} !important; border-right-color: ${sidebarNavColor} !important; }`,
      sidebarFontColor &&
        `.app-menu .navbar-nav .nav-link, .app-menu .menu-title, .app-menu .nav-sm .nav-link, .app-menu .nav-link svg, .app-menu .nav-link i, .app-menu .btn-vertical-sm-hover, .app-menu .hamburger-icon span { color: ${sidebarFontColor} !important; fill: ${sidebarFontColor} !important; }`,
      // Higher specificity than index.css's own .app-menu .nav-link.menu-
      // link:hover rule (which hardcodes #3e4f57/#fff), so this wins
      // regardless of source order.
      sidebarHoverBgColor &&
        `.app-menu.navbar-menu .nav-link.menu-link:hover { background-color: ${sidebarHoverBgColor} !important; }`,
      sidebarHoverFontColor &&
        `.app-menu.navbar-menu .nav-link.menu-link:hover, .app-menu.navbar-menu .nav-link.menu-link:hover i, .app-menu.navbar-menu .nav-link.menu-link:hover span { color: ${sidebarHoverFontColor} !important; fill: ${sidebarHoverFontColor} !important; }`,
      filterBgColor &&
        `.card-body.flr_note_bck, .placeholder.flr_note_bck_placeholder { background: ${filterBgColor} !important; background-color: ${filterBgColor} !important; }`,
      // Solid success/info buttons — pilot pages only, see
      // utils/whitelabelButtonTheme.ts.
      isWhitelabelUser &&
        isButtonThemePath(url.pathname) &&
        buildButtonThemeCss(
          whitelabelBranding?.button_bg_color,
          whitelabelBranding?.button_font_color
        ),
    ]
      .filter(Boolean)
      .join("\n") || null;

  // Memoized feature checks for better performance
  const featureFlags = useMemo(
    () => ({
      isDirectBookingIncluded:
        currentUser?.features?.some(
          (i: any) => i.feature === Features.FEATURE_DIRECT_BOOKING
        ) ?? false,
      isTravelFundIncluded:
        currentUser?.features?.some(
          (i: any) => i.feature === Features.FEATURE_FUND_REQUEST
        ) ?? false,
      isBookingHistoryIncluded:
        currentUser?.features?.some(
          (i: any) => i.feature === Features.FEATURE_DIRECT_BOOKING_HISTORY
        ) ?? false,
      isAffiliateIncluded:
        currentUser?.features?.some(
          (i: any) => i.feature === Features.FEATURE_AFFILIATE_DASHBOARD
        ) ?? false,
      isActivationCodeIncluded:
        currentUser?.features?.some(
          (i: any) => i.feature === Features.FEATURE_ACTIVATION_CODE
        ) ?? false,
    }),
    [currentUser?.features]
  );

  // Memoized tab checks
  const tabStates = useMemo(() => {
    const activeTab = url.pathname.split("/")[1];
    return {
      isAdminTab: ["agent-list", "branding-setup", "all-member-bookings-report", "travel-fund-deductions", "travelfund-monitor", "ticket-fund-monitoring", "email-logs", "sms-monitor", "itext-sms-monitor", "queue-monitor", "withdrawal-requests", "wallet-monitoring", "agent-income-monitoring", "image-generator", "payment-dashboard", "business-verifications"].includes(activeTab),
      isSmsLogsTab: ["sms-monitor", "itext-sms-monitor"].includes(activeTab),
      isActivityLogsTab: ["user-activity"].includes(activeTab),
      // Income Center stays expanded while on one of its pages.
      isIncomeCenterTab: [
        "income-summary",
        "affiliate-dashboard",
        "book-demo-requests",
        "insurance-income",
      ].includes(activeTab),
      isTrainingTab: ["create-training", "trainings"].includes(activeTab),
      travelInsuranceTab: ["travel-insurance", "insurance-income"].includes(activeTab),
      airlineFlightsTab: [
        "flight-booking",
        "booking-history-report",
      ].includes(activeTab),
      flightInquiryTab: ["flight-quotations", "booking-inquiries"].includes(
        activeTab
      ),
      rebrandingTab: [
        "e-ticket-generator",
        "e-ticket-history-report",
        "e-voucher-generator",
        "e-voucher-history-report",
        "flight-change",
        "add-baggage",
      ].includes(activeTab),
      visaProcessingTab: ["visa/create"].includes(activeTab),
      hotelBookingTab: [
        "hotel-booking",
        "hotel-booking-history-report",
      ].includes(activeTab),
      hotelBookingV2Tab: [
        "hotel-booking-v2",
        "hotel-my-bookings-v2",
      ].includes(activeTab),
    };
  }, [url.pathname]);

  // Memoized menu sections configuration
  const menuSections = useMemo(
    (): MenuSection[] => [
      {
        id: "airlineFlights",
        title: "Airline Flights",
        icon: "ri-plane-line",
        items: [
          {
            path: "/flight-booking",
            label: "Flight Booking",
            icon: "",
            dataKey: "flight-booking",
          },
          {
            path: "/booking-history-report",
            label: "Transaction Reports",
            icon: "",
            dataKey: "booking-history-report",
          },
        ],
      },
      {
        id: "hotels",
        title: "Hotel Booking",
        icon: "ri-file-list-3-line",
        items: [
          {
            path: "/hotel-booking",
            label: "Search Hotel",
            icon: "",
            dataKey: "hotel-booking",
          },
          {
            path: "/hotel-booking-history-report",
            label: "Transaction Reports",
            icon: "",
            dataKey: "hotel-booking-history-report",
          },
        ],
      },
      {
        id: "hotelBookingV2",
        title: "Hotel Booking v2",
        icon: "ri-hotel-line",
        items: [
          {
            path: "/hotel-booking-v2",
            label: "Search Hotel",
            icon: "",
            dataKey: "hotel-booking-v2",
          },
          {
            path: "/hotel-my-bookings-v2",
            label: "My Hotel Bookings",
            icon: "",
            dataKey: "hotel-my-bookings-v2",
          },
        ],
      },
      {
        id: "rebranding",
        title: "Rebranding",
        icon: "ri-palette-line",
        items: [
          {
            path: "/e-ticket-generator",
            label: "Flight Itinerary Builder",
            icon: "",
            dataKey: "e-ticket-generator",
          },
          {
            path: "/e-voucher-generator",
            label: "Hotel Voucher Builder",
            icon: "",
            dataKey: "e-voucher-generator",
          },
          {
            path: "/e-ticket-history-report",
            label: "Flight Transaction Reports",
            icon: "",
            dataKey: "e-ticket-history-report",
          },
          {
            path: "/e-voucher-history-report",
            label: "Hotel Transaction Reports",
            icon: "",
            dataKey: "e-voucher-history-report",
          },
          {
            path: "/flight-change",
            label: "Flight Change Manager",
            icon: "",
            dataKey: "flight-change",
          },
          {
            path: "/add-baggage",
            label: "Baggage Management",
            icon: "",
            dataKey: "add-baggage",
          },
        ],
      },
      {
        // Piloted alongside the Create Quote Link button on /flight-booking —
        // gated by showBookingInquiriesMenu (canUseFlightQuotation).
        id: "flightInquiry",
        title: "Flight Inquiry",
        icon: "ri-inbox-line",
        items: [
          {
            path: "/flight-quotations",
            label: "Quotation List",
            icon: "",
            dataKey: "flight-quotations",
            hidden: !showBookingInquiriesMenu,
          },
          {
            path: "/booking-inquiries",
            label: "Booking Inquiries",
            icon: "",
            dataKey: "booking-inquiries",
            hidden: !showBookingInquiriesMenu,
          },
        ],
      },
    ],
    // These flags depend on currentUser.account_id, which loads
    // asynchronously after redux hydration — this array must recompute once
    // it's available, or the account check freezes at its pre-login value
    // (always hidden) since it was previously []-memoized once at mount.
    [showHotelV2Menu, showBookingInquiriesMenu]
  );

  // Dropdowns in the whitelabel menu — the pages the whitelabel spec keeps.
  const whitelabelSections = useMemo(
    (): Record<string, MenuSection> => ({
      travelInsurance: {
        id: "wlTravelInsurance",
        title: "Travel Insurance",
        icon: "ri-shield-check-line",
        items: [
          { path: "/travel-insurance", label: "Travel Insurance", icon: "", dataKey: "travel-insurance" },
          { path: "/travel-insurance/history", label: "Transaction History", icon: "", dataKey: "travel-insurance-history" },
          { path: "/insurance-income", label: "Insurance Income", icon: "", dataKey: "insurance-income" },
        ],
      },
      activityLogs: {
        id: "wlActivityLogs",
        title: "My Activity Logs",
        icon: "ri-history-line",
        items: [
          { path: "/user-activity?tab=email", label: "Email Logs", icon: "", dataKey: "user-activity-email" },
          { path: "/user-activity?tab=sms", label: "SMS Logs", icon: "", dataKey: "user-activity-sms" },
        ],
      },
      adminAccount: {
        id: "wlAdminAccount",
        title: "Admin Account",
        icon: "ri-account-circle-line",
        items: [
          { path: "/branding-setup", label: "Branding Setup", icon: "", dataKey: "branding-setup" },
          { path: "/agent-list", label: "Agent List/Report", icon: "", dataKey: "agent-list" },
          { path: "/all-member-bookings-report", label: "All Member Bookings", icon: "", dataKey: "all-member-bookings-report" },
          { path: "/travelfund-monitor", label: "Travel Fund Monitor", icon: "", dataKey: "travelfund-monitor" },
          { path: "/email-logs", label: "Email Logs", icon: "", dataKey: "email-logs" },
          { path: "/sms-monitor", label: "SMS Logs", icon: "", dataKey: "sms-monitor" },
          { path: "/withdrawal-requests", label: "Withdrawal Requests", icon: "", dataKey: "withdrawal-requests" },
          { path: "/payment-dashboard", label: "Payment Dashboard", icon: "", dataKey: "payment-dashboard" },
        ],
      },
    }),
    []
  );

  // Optimized click outside handler
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(event.target as Node)
    ) {
      document.body.classList.remove("vertical-sidebar-enable");
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  // Extracted feature check function for reusability
  const isFeatureIncluded = useCallback(
    (feature: Features): boolean => {
      return (
        currentUser?.features?.some((i: any) => i.feature === feature) ?? false
      );
    },
    [currentUser?.features]
  );

  useEffect(() => {
    if (!auth.authToken || auth.authToken === "") {
      logoutHandler();
    }
    if (
      url.pathname.split("/")[2] === undefined ||
      url.pathname.split("/")[2] !== "main-course"
    ) {
      localStorage.removeItem("mainCourse");
      localStorage.removeItem("videoProgress");
    }
    setActiveTab(url.pathname.split("/")[1]);
  }, [auth.authToken, url]);

  // Optimized logout handler with better error handling
  const logoutHandler = useCallback(async () => {
    try {
      dispatch(setLoader(true));
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.post("/api/auth/logout");

      if (response.data.result.success) {
        await Promise.all([
          dispatch(setLoader(false)),
          dispatch(logout()),
          dispatch(clearFlightSearch()),
        ]);
        window.location.href = getSignInUrl();
      } else {
        dispatch(setLoader(false));
      }
    } catch (error) {
      dispatch(setLoader(false));
      dispatch(logout());
      window.location.href = getSignInUrl();
    }
  }, [dispatch]);

  // Extracted utility function
  const resetEditData = useCallback(() => {
    if (localStorage.getItem("editData") !== null) {
      localStorage.removeItem("editData");
    }
  }, []);

  // Component for rendering dropdown sections to reduce repetition
  const DropdownSection: React.FC<{
    section: MenuSection;
    isDropdownActive: boolean;
  }> = ({ section, isDropdownActive }) => (
    <li className="nav-item">
      <a
        className={`nav-link menu-link ${
          isDropdownActive ? "drop-down-active" : "collapsed"
        }`}
        href={`#${section.id}`}
        data-bs-toggle="collapse"
        role="button"
        aria-expanded={isDropdownActive}
        aria-controls={section.id}
      >
        <i className={section.icon}></i>{" "}
        <span data-key="t-authentication">{section.title}</span>
      </a>
      <div
        className={`menu-dropdown collapse ${isDropdownActive ? "show" : ""}`}
        id={section.id}
      >
        <ul className="nav nav-sm flex-column">
          {section.items
            .filter((item) => !item.hidden)
            .map((item) => (
            <li
              key={item.path}
              className={`nav-item ${
                item.hideOnMobile ? "hide-on-mobile" : ""
              }`}
            >
              <Link
                to={item.path}
                className={`nav-link menu-link ${
                  isActive === item.path.slice(1) ? "active" : ""
                }`}
                onClick={item.onClick}
              >
                <span data-key={item.dataKey}>{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );

  return (
    <div className="app-menu navbar-menu" ref={sidebarRef}>
      {sidebarThemeCss && <style>{sidebarThemeCss}</style>}
      <Loader />
      <InternetConnectivityCheck />
      <div className="navbar-brand-box">
        <Link to="/" className="logo logo-dark">
          <span className="logo-sm">
            <img
              src={sidebarLogo}
              alt="Wanderlinx banner"
              height="22"
            />
          </span>
          <span className="logo-lg">
            <img
              src={sidebarLogo}
              alt="Wanderlinx banner"
              height="17"
            />
          </span>
        </Link>
        <Link to="/" className="logo logo-light mt-2">
          <span className="logo-sm">
            <img
              src={sidebarLogo}
              alt="mjc-logo"
              height="22"
            />
          </span>
          <span className="logo-lg">
            <img
              src={sidebarLogo}
              alt="mjc-logo"
              height="60"
            />
          </span>
        </Link>
        <button
          type="button"
          className="btn btn-sm p-0 fs-20 header-item float-end btn-vertical-sm-hover"
          id="vertical-hover"
        >
          <i className="ri-record-circle-line"></i>
        </button>
      </div>

      <div id="scrollbar">
        <div className="container-fluid">
          <div id="two-column-menu"></div>
          <ul className="navbar-nav" id="navbar-nav">
            <li className="menu-title">
              <span data-key="t-menu">{sidebarBrandName}</span>
            </li>

            {isEtgTestAccount ? (
              <>
                {/* ETG's own test account — Hotel Booking v2 only, nothing
                    else in the app. See isEtgTestAccount above. */}
                <DropdownSection
                  section={menuSections[2]}
                  isDropdownActive={tabStates.hotelBookingV2Tab}
                />
                <li className="nav-item" onClick={logoutHandler}>
                  <Link to="/logout" className="menu-link nav-link label-cursor">
                    <i className="ri-logout-circle-line"></i>
                    <span>Logout</span>
                  </Link>
                </li>
              </>
            ) : isWhitelabelUser ? (
              <>
                {/* Whitelabel account — only the pages the whitelabel spec
                    keeps. See whitelabelSections above. */}
                <DropdownSection
                  section={menuSections[0]}
                  isDropdownActive={tabStates.airlineFlightsTab}
                />
                {/* Flight Inquiry — whitelabel admin only (canUseFlightQuotation
                    returns false for whitelabel agents). */}
                {showBookingInquiriesMenu && (
                  <DropdownSection
                    section={menuSections[4]}
                    isDropdownActive={tabStates.flightInquiryTab}
                  />
                )}
                <DropdownSection
                  section={menuSections[1]}
                  isDropdownActive={tabStates.hotelBookingTab}
                />
                <DropdownSection
                  section={whitelabelSections.travelInsurance}
                  isDropdownActive={tabStates.travelInsuranceTab}
                />
                {[
                  { path: "/calendar-flight", icon: "ri-calendar-line", label: "Flight Calendar", className: "hide-on-mobile" },
                  { path: "/manage-booking", icon: "ri-customer-service-2-line", label: "Manage Booking" },
                  { path: "/frequent-travelers", icon: "ri-contacts-fill", label: "Frequent Travelers" },
                  { path: "/travel-fund", icon: "ri-money-dollar-circle-line", label: "Travel Fund" },
                ].map((link) => (
                  <li key={link.path} className={`nav-item ${link.className ?? ""}`}>
                    <Link
                      to={link.path}
                      className={`nav-link menu-link ${isActive === link.path.slice(1) ? "active" : ""}`}
                    >
                      <i className={link.icon}></i>
                      <span data-key={link.path.slice(1)}>{link.label}</span>
                    </Link>
                  </li>
                ))}
                {/* Rebranding — whitelabel admin only (agents are blocked by
                    User::canUseRebranding, so showRebrandingMenu is false). */}
                {isWhitelabelAdminUser && showRebrandingMenu && (
                  <DropdownSection
                    section={menuSections[3]}
                    isDropdownActive={tabStates.rebrandingTab}
                  />
                )}
                <DropdownSection
                  section={whitelabelSections.activityLogs}
                  isDropdownActive={tabStates.isActivityLogsTab}
                />
                {isWhitelabelAdminUser && (
                  <DropdownSection
                    section={whitelabelSections.adminAccount}
                    isDropdownActive={tabStates.isAdminTab}
                  />
                )}
                {[
                  { path: "/account-setting", icon: "ri-list-settings-line", label: "Account Setting", show: true },
                  { path: "/payment-history", icon: "ri-secure-payment-line", label: "Payment History", show: isWhitelabelAdminUser },
                  { path: "/terms-condition", icon: "ri-newspaper-fill", label: "Terms & Condition", show: true },
                ]
                  .filter((link) => link.show)
                  .map((link) => (
                    <li key={link.path} className="nav-item">
                      <Link
                        to={link.path}
                        className={`nav-link menu-link ${isActive === link.path.slice(1) ? "active" : ""}`}
                      >
                        <i className={link.icon}></i>
                        <span data-key={link.path.slice(1)}>{link.label}</span>
                      </Link>
                    </li>
                  ))}
                <li className="nav-item" onClick={logoutHandler}>
                  <Link to="/logout" className="menu-link nav-link label-cursor">
                    <i className="ri-logout-circle-line"></i>
                    <span>Logout</span>
                  </Link>
                </li>
              </>
            ) : isInsuranceProvider ? (
              <>
                <li className="nav-item">
                  <a
                    className={`nav-link menu-link ${
                      tabStates.travelInsuranceTab
                        ? "drop-down-active"
                        : "collapsed"
                    }`}
                    href="#sidebarTravelInsurance"
                    data-bs-toggle="collapse"
                    role="button"
                    aria-expanded={tabStates.travelInsuranceTab}
                    aria-controls="sidebarTravelInsurance"
                  >
                    <i className="ri-shield-check-line"></i>{" "}
                    <span data-key="t-travel-insurance">Travel Insurance</span>
                  </a>
                  <div
                    className={`menu-dropdown collapse ${
                      tabStates.travelInsuranceTab ? "show" : ""
                    }`}
                    id="sidebarTravelInsurance"
                  >
                    <ul className="nav nav-sm flex-column">
                      <li className="nav-item">
                        <Link
                          to="/travel-insurance"
                          className={`nav-link menu-link ${
                            isActive === "travel-insurance" &&
                            url.pathname === "/travel-insurance"
                              ? "active"
                              : ""
                          }`}
                        >
                          <span data-key="travel-insurance">
                            Travel Insurance
                          </span>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          to="/travel-insurance/history"
                          className={`nav-link menu-link ${
                            url.pathname === "/travel-insurance/history"
                              ? "active"
                              : ""
                          }`}
                        >
                          <span data-key="travel-insurance-history">
                            Transaction History
                          </span>
                        </Link>
                      </li>
                    </ul>
                  </div>
                </li>
                <li className="nav-item" onClick={logoutHandler}>
                  <Link to="/logout" className="menu-link nav-link label-cursor">
                    <i className="ri-logout-circle-line"></i>
                    <span>Logout</span>
                  </Link>
                </li>
              </>
            ) : (
              <>
                {/* 1. Airline Flights */}
                <DropdownSection
                  section={menuSections[0]}
                  isDropdownActive={tabStates.airlineFlightsTab}
                />

                {/* 1b. Flight Inquiry */}
                {showBookingInquiriesMenu && (
                  <DropdownSection
                    section={menuSections[4]}
                    isDropdownActive={tabStates.flightInquiryTab}
                  />
                )}

                {/* 2. Hotel Booking */}
                <DropdownSection
                  section={menuSections[1]}
                  isDropdownActive={tabStates.hotelBookingTab}
                />

                {/* 2b. Hotel Booking v2 */}
                {showHotelV2Menu && (
                  <DropdownSection
                    section={menuSections[2]}
                    isDropdownActive={tabStates.hotelBookingV2Tab}
                  />
                )}

                {/* 3. Travel Insurance */}
                <li className="nav-item">
                  <a
                    className={`nav-link menu-link ${
                      tabStates.travelInsuranceTab
                        ? "drop-down-active"
                        : "collapsed"
                    }`}
                    href="#sidebarTravelInsurance"
                    data-bs-toggle="collapse"
                    role="button"
                    aria-expanded={tabStates.travelInsuranceTab}
                    aria-controls="sidebarTravelInsurance"
                  >
                    <i className="ri-shield-check-line"></i>{" "}
                    <span data-key="t-travel-insurance">Travel Insurance</span>
                  </a>
                  <div
                    className={`menu-dropdown collapse ${
                      tabStates.travelInsuranceTab ? "show" : ""
                    }`}
                    id="sidebarTravelInsurance"
                  >
                    <ul className="nav nav-sm flex-column">
                      <li className="nav-item">
                        <Link
                          to="/travel-insurance"
                          className={`nav-link menu-link ${
                            isActive === "travel-insurance" &&
                            url.pathname === "/travel-insurance"
                              ? "active"
                              : ""
                          }`}
                        >
                          <span data-key="travel-insurance">
                            Travel Insurance
                          </span>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          to="/travel-insurance/history"
                          className={`nav-link menu-link ${
                            url.pathname === "/travel-insurance/history"
                              ? "active"
                              : ""
                          }`}
                        >
                          <span data-key="travel-insurance-history">
                            Transaction History
                          </span>
                        </Link>
                      </li>
                    </ul>
                  </div>
                </li>

                {/* 4. Visa Processing */}
                <li className="nav-item">
                  <a
                    className={`nav-link menu-link ${
                      tabStates.visaProcessingTab
                        ? "drop-down-active"
                        : "collapsed"
                    }`}
                    href="#visaProcessing"
                    data-bs-toggle="collapse"
                    role="button"
                    aria-expanded={tabStates.visaProcessingTab}
                    aria-controls="visaProcessing"
                  >
                    <i className="ri-pass-valid-fill"></i>{" "}
                    <span data-key="t-authentication">Visa Processing</span>
                  </a>
                  <div
                    className={`menu-dropdown collapse ${
                      tabStates.visaProcessingTab ? "show" : ""
                    }`}
                    id="visaProcessing"
                  >
                    <ul className="nav nav-sm flex-column">
                      {currentUser.is_visa_enabled && (
                        <>
                          {currentUser?.roles === "Admin" && (
                            <>
                              <li className="nav-item">
                                <Link
                                  to="/visa/create"
                                  className={`nav-link menu-link ${
                                    isActive === "visa/create" ? "active" : ""
                                  }`}
                                >
                                  <span data-key="visa/create">Visa Setup</span>
                                </Link>
                              </li>
                              <li className="nav-item">
                                <Link
                                  to="/visa/process/list"
                                  className={`nav-link menu-link ${
                                    isActive === "visa/process/list" ? "active" : ""
                                  }`}
                                >
                                  <span data-key="visa/process/list">
                                    Manage Visa
                                  </span>
                                </Link>
                              </li>
                              <li className="nav-item">
                                <Link
                                  to="/visa/request/list"
                                  className={`nav-link menu-link ${
                                    isActive === "visa/request/list" ? "active" : ""
                                  }`}
                                >
                                  <span data-key="visa/process/list">
                                    Manage Application
                                  </span>
                                </Link>
                              </li>
                            </>
                          )}
                          <li className="nav-item">
                            <Link
                              to="/visa/book"
                              className={`nav-link menu-link ${
                                isActive === "visa/book" ? "active" : ""
                              }`}
                            >
                              <span data-key="visa/book">Visa Directory</span>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/visa/list"
                              className={`nav-link menu-link ${
                                isActive === "visa/list" ? "active" : ""
                              }`}
                            >
                              <span data-key="visa/list">Application List</span>
                            </Link>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </li>

                {/* Tour Package Activation — hidden once the user has activated their own TripZynx account */}
                {!currentUser?.tripzynx_id && (
                  <li className="nav-item">
                    <Link
                      to="/tour-package-activation"
                      className={`nav-link menu-link nav-link-highlight ${
                        isActive === "tour-package-activation" ? "active" : ""
                      }`}
                    >
                      <i className="ri-map-2-line"></i>
                      <span data-key="tour-package-activation">Tour Package Activation</span>
                    </Link>
                  </li>
                )}

                {/* Tours and Activities (TripZynx) */}
                {currentUser?.tripzynx_id && (
                  <li className="nav-item">
                    <Link
                      to="/tours-and-activities"
                      className={`nav-link menu-link ${
                        isActive === "tours-and-activities" ? "active" : ""
                      }`}
                    >
                      <i className="ri-compass-3-line"></i>
                      <span data-key="tours-and-activities">Tours and Activities</span>
                    </Link>
                  </li>
                )}

                {/* 5. Rebranding */}
                {showRebrandingMenu && (
                  <DropdownSection
                    section={menuSections[3]}
                    isDropdownActive={tabStates.rebrandingTab}
                  />
                )}

                {/* 6. Flight Calendar */}
                <li className="nav-item hide-on-mobile">
                  <Link
                    to="/calendar-flight"
                    className={`nav-link menu-link ${
                      isActive === "calendar-flight" ? "active" : ""
                    }`}
                  >
                    <i className="ri-calendar-line"></i>
                    <span data-key="calendar-flight">Flight Calendar</span>
                  </Link>
                </li>

                {/* 7. Frequent Travelers */}
                <li className="nav-item">
                  <Link
                    to="/frequent-travelers"
                    className={`nav-link menu-link ${
                      isActive === "frequent-travelers" ? "active" : ""
                    }`}
                  >
                    <i className="ri-contacts-fill"></i>
                    <span data-key="account-setting">Frequent Travelers</span>
                  </Link>
                </li>

                {/* 8. Travel Fund */}
                <li className="nav-item">
                  <Link
                    to="/travel-fund"
                    className={`nav-link menu-link ${
                      isActive === "travel-fund" ? "active" : ""
                    }`}
                  >
                    <i className="ri-money-dollar-circle-line"></i>
                    <span data-key="account-setting">Travel Fund</span>
                  </Link>
                </li>

                {/* 9. Credits Account */}
                <li className="nav-item">
                  <Link
                    to="/billing-history"
                    className={`nav-link menu-link ${
                      isActive === "billing-history" ? "active" : ""
                    }`}
                  >
                    <i className="ri-wallet-line"></i>
                    <span data-key="account-setting">Credits Account</span>
                  </Link>
                </li>

                {/* 10. Activation Code */}
                <li className="nav-item">
                  <Link
                    to="/activation-code"
                    className={`nav-link menu-link ${
                      isActive === "activation-code" ? "active" : ""
                    }`}
                  >
                    <i className="ri-barcode-fill"></i>
                    <span data-key="account-setting">Activation Code</span>
                  </Link>
                </li>

                {/* 11. Income Center — open to all plans (Standard,
                    Business, Enterprise), not just Business. */}
                {([1, 2, 3].includes(currentUser?.company?.subscription_id) ||
                  currentUser?.roles === "Admin") && (
                  <li className="nav-item">
                    <a
                      className={`nav-link menu-link ${
                        tabStates.isIncomeCenterTab ? "drop-down-active" : "collapsed"
                      }`}
                      href="#sidebarAffiliate"
                      data-bs-toggle="collapse"
                      role="button"
                      aria-expanded={tabStates.isIncomeCenterTab}
                      aria-controls="sidebarAffiliate"
                    >
                      <i className="ri-dashboard-line"></i>
                      <span data-key="search-flight">Income Center</span>
                    </a>
                    <div
                      className={`menu-dropdown collapse ${
                        tabStates.isIncomeCenterTab ? "show" : ""
                      }`}
                      id="sidebarAffiliate"
                    >
                      <ul className="nav nav-sm flex-column">
                        <li className="nav-item">
                          <Link
                            to="/income-summary"
                            className={`nav-link ${url.pathname === "/income-summary" ? "active" : ""}`}
                          >
                            Income Summary
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link
                            to="/affiliate-dashboard"
                            className={`nav-link ${url.pathname === "/affiliate-dashboard" ? "active" : ""}`}
                          >
                            Affiliate Income
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link
                            to="/book-demo-requests"
                            className={`nav-link ${url.pathname === "/book-demo-requests" ? "active" : ""}`}
                          >
                            Leads List
                          </Link>
                        </li>
                        <li className="nav-item">
                          <Link
                            to="/insurance-income"
                            className={`nav-link ${url.pathname === "/insurance-income" ? "active" : ""}`}
                          >
                            Insurance Income
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </li>
                )}

                {/* 12. My Activity Logs */}
                <li className="nav-item">
                  <a
                    className={`nav-link menu-link ${
                      tabStates.isActivityLogsTab
                        ? "drop-down-active"
                        : "collapsed"
                    }`}
                    href="#sidebarActivityLogs"
                    data-bs-toggle="collapse"
                    role="button"
                    aria-expanded={tabStates.isActivityLogsTab}
                    aria-controls="sidebarActivityLogs"
                  >
                    <i className="ri-history-line"></i>{" "}
                    <span data-key="user-activity">My Activity Logs</span>
                  </a>
                  <div
                    className={`menu-dropdown collapse ${
                      tabStates.isActivityLogsTab ? "show" : ""
                    }`}
                    id="sidebarActivityLogs"
                  >
                    <ul className="nav nav-sm flex-column">
                      <li className="nav-item">
                        <Link
                          to="/user-activity?tab=email"
                          className={`nav-link menu-link ${
                            isActive === "user-activity" &&
                            url.search !== "?tab=sms"
                              ? "active"
                              : ""
                          }`}
                        >
                          <span data-key="user-activity-email">
                            Email Logs
                          </span>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          to="/user-activity?tab=sms"
                          className={`nav-link menu-link ${
                            isActive === "user-activity" &&
                            url.search === "?tab=sms"
                              ? "active"
                              : ""
                          }`}
                        >
                          <span data-key="user-activity-sms">SMS Logs</span>
                        </Link>
                      </li>
                    </ul>
                  </div>
                </li>

                {/* Admin-only items */}
                {currentUser?.roles === "Admin" && (
                  <div>
                    <li className="nav-item">
                      <a
                        className={`nav-link menu-link ${
                          tabStates.isTrainingTab
                            ? "drop-down-active"
                            : "collapsed"
                        }`}
                        href="#sidebarTraining"
                        data-bs-toggle="collapse"
                        role="button"
                        aria-expanded={tabStates.isTrainingTab}
                        aria-controls="sidebarTraining"
                      >
                        <i className="ri-slideshow-4-line"></i>{" "}
                        <span data-key="t-authentication">Training</span>
                      </a>
                      <div
                        className={`menu-dropdown collapse ${
                          tabStates.isTrainingTab ? "show" : ""
                        }`}
                        id="sidebarTraining"
                      >
                        <ul className="nav nav-sm flex-column">
                          <li className="nav-item">
                            <Link
                              to="/create-training"
                              className={`nav-link menu-link ${
                                isActive === "create-training" ? "active" : ""
                              }`}
                              onClick={resetEditData}
                            >
                              <span data-key="create-training">Create</span>{" "}
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/trainings"
                              className={`nav-link menu-link ${
                                isActive === "trainings" ? "active" : ""
                              }`}
                            >
                              <span data-key="trainings">List</span>{" "}
                            </Link>
                          </li>
                        </ul>
                      </div>
                    </li>

                    <li className="nav-item">
                      <a
                        className={`nav-link menu-link ${
                          tabStates.isAdminTab
                            ? "drop-down-active"
                            : "collapsed"
                        }`}
                        href="#sidebarAuth"
                        data-bs-toggle="collapse"
                        role="button"
                        aria-expanded={tabStates.isAdminTab}
                        aria-controls="sidebarAuth"
                      >
                        <i className="ri-account-circle-line"></i>{" "}
                        <span data-key="t-authentication">Admin Account</span>
                      </a>
                      <div
                        className={`menu-dropdown collapse ${
                          tabStates.isAdminTab ? "show" : ""
                        }`}
                        id="sidebarAuth"
                      >
                        <ul className="nav nav-sm flex-column">
                          <li className="nav-item">
                            <Link
                              to="/agent-list"
                              className={`nav-link menu-link ${
                                isActive === "agent-list" &&
                                url.pathname !== "/agent-list/whitelabel"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              <span data-key="Create_Itinerary">
                                Agent List/Report
                              </span>{" "}
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/agent-list/whitelabel"
                              className={`nav-link menu-link ${
                                url.pathname === "/agent-list/whitelabel"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              <span data-key="agent-list-whitelabel">
                                Whitelabel Agents
                              </span>{" "}
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/all-member-bookings-report"
                              className={`nav-link menu-link ${
                                isActive === "all-member-bookings-report"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              <span data-key="all-member-bookings-report">
                                All Member Bookings
                              </span>{" "}
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/ticket-fund-monitoring"
                              className={`nav-link menu-link ${
                                isActive === "ticket-fund-monitoring"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              <span data-key="ticket-fund-monitoring">
                                Ticket Fund Monitoring
                              </span>{" "}
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/travelfund-monitor"
                              className={`nav-link menu-link ${
                                isActive === "travelfund-monitor"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              <span data-key="travelfund-monitor">
                                Travel Fund Monitor
                              </span>{" "}
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/agent-income-monitoring"
                              className={`nav-link menu-link ${
                                isActive === "agent-income-monitoring"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              <span data-key="agent-income-monitoring">
                                Agent Income Monitoring
                              </span>{" "}
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/email-logs"
                              className={`nav-link menu-link ${
                                isActive === "email-logs" ? "active" : ""
                              }`}
                            >
                              <span data-key="email-logs">Email Logs</span>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <a
                              className={`nav-link menu-link ${
                                tabStates.isSmsLogsTab
                                  ? "drop-down-active"
                                  : "collapsed"
                              }`}
                              href="#sidebarSmsLogs"
                              data-bs-toggle="collapse"
                              role="button"
                              aria-expanded={tabStates.isSmsLogsTab}
                              aria-controls="sidebarSmsLogs"
                            >
                              <span data-key="sms-logs">SMS Logs</span>
                            </a>
                            <div
                              className={`menu-dropdown collapse ${
                                tabStates.isSmsLogsTab ? "show" : ""
                              }`}
                              id="sidebarSmsLogs"
                            >
                              <ul className="nav nav-sm flex-column">
                                <li className="nav-item">
                                  <Link
                                    to="/itext-sms-monitor"
                                    className={`nav-link menu-link ${
                                      isActive === "itext-sms-monitor" ? "active" : ""
                                    }`}
                                  >
                                    <span data-key="itext-sms-monitor">
                                      ItextMo SMS Logs
                                    </span>
                                  </Link>
                                </li>
                                <li className="nav-item">
                                  <Link
                                    to="/sms-monitor"
                                    className={`nav-link menu-link ${
                                      isActive === "sms-monitor" ? "active" : ""
                                    }`}
                                  >
                                    <span data-key="sms-monitor">
                                      TextBox SMS Logs
                                    </span>
                                  </Link>
                                </li>
                              </ul>
                            </div>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/book-demo-requests"
                              className={`nav-link menu-link ${
                                isActive === "book-demo-requests" ? "active" : ""
                              }`}
                            >
                              <span data-key="book-demo-requests">Book Demo Requests</span>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/business-verifications"
                              className={`nav-link menu-link ${
                                isActive === "business-verifications" ? "active" : ""
                              }`}
                            >
                              <span data-key="business-verifications">Business Verifications</span>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/withdrawal-requests"
                              className={`nav-link menu-link ${
                                isActive === "withdrawal-requests" ? "active" : ""
                              }`}
                            >
                              <span data-key="withdrawal-requests">Withdrawal Requests</span>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/image-generator"
                              className={`nav-link menu-link ${
                                isActive === "image-generator" ? "active" : ""
                              }`}
                            >
                              <span data-key="image-generator">AI Image Generator</span>
                            </Link>
                          </li>
                          {showPaymentDashboardLink && (
                            <li className="nav-item">
                              <Link
                                to="/payment-dashboard"
                                className={`nav-link menu-link ${
                                  isActive === "payment-dashboard" ? "active" : ""
                                }`}
                              >
                                <span data-key="payment-dashboard">Payment Dashboard</span>
                              </Link>
                            </li>
                          )}
                        </ul>
                      </div>
                    </li>
                  </div>
                )}

                {/* 13. Account Setting */}
                <li className="nav-item">
                  <Link
                    to="/account-setting"
                    className={`nav-link menu-link ${
                      isActive === "account-setting" ? "active" : ""
                    }`}
                  >
                    <i className="ri-list-settings-line"></i>
                    <span data-key="account-setting">Account Setting</span>
                  </Link>
                </li>

                {/* 14. Provider List */}
                <li className="nav-item">
                  <Link
                    to="/provider-list"
                    className={`nav-link menu-link ${
                      isActive === "provider-list" ? "active" : ""
                    }`}
                  >
                    <i className="ri-file-list-3-fill"></i>
                    <span data-key="activation-code">Provider list</span>
                  </Link>
                </li>

                {/* 15. eLearnings */}
                <li className="nav-item">
                  <Link
                    to="/e-learnings"
                    className={`nav-link menu-link ${
                      isActive === "e-learnings" ? "active" : ""
                    }`}
                  >
                    <i className="ri-play-circle-line"></i>
                    <span data-key="e-learnings">eLearnings</span>
                  </Link>
                </li>

                {currentUser?.roles === "Admin" && (
                  <li className="nav-item">
                    <Link
                      to="/payment-history"
                      className={`nav-link menu-link ${
                        isActive === "payment-history" ? "active" : ""
                      }`}
                    >
                      <i className="ri-secure-payment-line"></i>
                      <span data-key="account-setting">Payment History</span>
                    </Link>
                  </li>
                )}

                {/* 15. Terms & Condition */}
                <li className="nav-item">
                  <Link
                    to="/terms-condition"
                    className={`nav-link menu-link ${
                      isActive === "terms-condition" ? "active" : ""
                    }`}
                  >
                    <i className="ri-newspaper-fill"></i>
                    <span data-key="search-flight">Terms & Condition</span>
                  </Link>
                </li>

                <li className="nav-item" onClick={logoutHandler}>
                  <Link to="/logout" className="menu-link nav-link label-cursor">
                    <i className="ri-logout-circle-line"></i>
                    <span>Logout</span>
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>

      <div className="sidebar-background"></div>
      <Footer />
    </div>
  );
};

export default AppMenu;
